// src/pages/admin/UsersManagement.tsx
import { useEffect, useState } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import { useAuth } from '../../../hooks/useAuth';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface User {
  id: string;
  fullName: string;
  email: string;
  role: string;
  telephone: string;
  status: 'Actif' | 'Inactif' | 'Bloqué';
  employe?: {
    salaire_base: number;
  };
  lastLogin: string;
  createdAt: string;
}


interface FormData {
  fullName: string;
  email: string;
  role: string;
  salaire_base: number | '';
  telephone: string;
  status: 'Actif' | 'Inactif' | 'Bloqué';
}
export default function UsersManagement() {
  const { signOut } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    role: 'Commercial',
    status: 'Actif',
    salaire_base: '',
    telephone: '',
  });

  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const roles = ['Administrateur', 'Commercial', 'Magasinier', 'Comptable'];

  /* ================= LOAD USERS ================= */

  const loadUsers = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterRole !== 'all') params.append('role', filterRole);
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const res = await getResources(`users?${params.toString()}`);

      setUsers(res.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        await signOut();
      }
      notify('error', 'Impossible de charger les utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [search, filterRole, filterStatus]);

  /* ================= HELPERS ================= */

  const notify = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const openForm = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        fullName: user.fullName,
        email: user.email,
        role: user.role,
        status: user.status,
        salaire_base: user.employe?.salaire_base ?? '',
        telephone: user.telephone,
      });
    } else {
      setEditingUser(null);
      setFormData({
        fullName: '',
        email: '',
        role: 'Commercial',
        salaire_base: '',
        status: 'Actif',
        telephone: '',
      });
    }
    setShowForm(true);
  };

  /* ================= SUBMIT ================= */

  const submitForm = async () => {
    try {
      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        telephone: formData.telephone || null,
        salaire_base: Number(formData.salaire_base),
        role: formData.role,
      };

      if (editingUser) {
        await updateResource(`users`,editingUser.id, payload);
        notify('success', 'Utilisateur modifié avec succès');
      } else {
        await createResource(`users`, payload);
        notify('success', 'Utilisateur créé. Email d’activation envoyé.');
      }

      setShowForm(false);
      loadUsers();
    } catch (err: any) {
      notify('error', err.response?.data?.message || 'Erreur serveur');
    }
  };

  /* ================= ACTIONS ================= */

  const resetPassword = async (id: string) => {
    if (!confirm('Envoyer un email de réinitialisation ?')) return;
    try {
      await createResource(`users/${id}/reset-password`, {});
      notify('success', 'Email de réinitialisation envoyé');
      
      loadUsers();
    } catch {
      notify('error', 'Échec de l’envoi');
    }
  };

  const toggleStatus = async (id: string) => {
    try {
      const resp = await createResource(
        `users/${id}/toggle-status`,
        {},
      );

      loadUsers();
      notify('success', resp.data?.message || 'Statut mis à jour');
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        'Erreur lors de la modification du statut';

      notify('error', message);
    }
  };

  const statusColor = (s: string) =>
    s === 'Actif'
      ? 'bg-green-100 text-green-800'
      : s === 'Bloqué'
      ? 'bg-red-100 text-red-800'
      : 'bg-gray-100 text-gray-800';

  /* ================= RENDER ================= */

  return (
    <div className="p-6 space-y-6">
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg text-white ${
          notification.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Gestion des utilisateurs</h1>
        <button onClick={() => openForm()} className="bg-blue-600 text-white px-5 py-2 rounded-lg">
          Nouvel utilisateur
        </button>
      </div>

      {/* Filtres */}
      <div className="flex flex-col md:flex-row gap-4">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher..."
          className="flex-1 px-4 py-2 border rounded-lg"
        />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="px-4 py-2 border rounded-lg">
          <option value="all">Tous les rôles</option>
          {roles.map(r => <option key={r}>{r}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-4 py-2 border rounded-lg">
          <option value="all">Tous les statuts</option>
          <option value="Actif">Actif</option>
          <option value="Inactif">Inactif</option>
          <option value="Bloqué">Bloqué</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-center py-10">Chargement...</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">Utilisateur</th>
                <th className="px-6 py-3">Rôle</th>
                <th className="px-6 py-3">Statut</th>
                <th className="px-6 py-3">Dernière connexion</th>
                <th className="px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium">{u.fullName}</div>
                    <div className="text-sm text-gray-500">{u.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">{u.role}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 text-xs rounded-full ${statusColor(u.status)}`}>{u.status}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {u.lastLogin === 'Jamais'
                      ? 'Jamais'
                      : format(new Date(u.lastLogin), 'dd MMM yyyy HH:mm', { locale: fr })}
                  </td>
                  <td className="px-6 py-4 flex gap-3">
                    <button onClick={() => openForm(u)} className="text-blue-600"><i className="ri-edit-line"></i></button>
                    <button onClick={() => resetPassword(u.id)} className="text-orange-600"><i className="ri-lock-password-line"></i></button>
                    <button onClick={() => toggleStatus(u.id)} className={u.status === 'Actif' ? 'text-red-600' : 'text-green-600'}>
                      <i className={u.status === 'Actif' ? 'ri-user-forbid-line' : 'ri-user-follow-line'}></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingUser ? 'Modifier utilisateur' : 'Nouvel utilisateur'}
            </h3>

            <div className="space-y-4">
              <input
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Nom complet"
                value={formData.fullName}
                onChange={e => setFormData({ ...formData, fullName: e.target.value })}
              />

              <input
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Email"
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
              />

              <input
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Téléphone (optionnel)"
                value={formData.telephone || ''}
                onChange={e => setFormData({ ...formData, telephone: e.target.value })}
              />

              <select
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.role}
                onChange={e => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="">Sélectionner un rôle</option>
                {roles.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>

              <input
                type="number"
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Salaire de base"
                value={formData.salaire_base}
                onChange={e =>
                  setFormData({
                    ...formData,
                    salaire_base: e.target.value === '' ? '' : Number(e.target.value),
                  })
                }
              />

            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 border rounded-lg"
              >
                Annuler
              </button>

              <button
                onClick={submitForm}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg"
              >
                {editingUser ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
